import type {
  Blob2DInPatchUnits,
  OrientedBlob2DInPatchUnits,
} from './Blob2DInPatchUnits';

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;

  return v;
}

export type UnletterboxContext = {
  gridH: number;
  gridW: number;
  inputH: number;
  inputW: number;
  paddingTop: number;
  paddingLeft: number;
  originalW: number;
  originalH: number;
};

export type UnletterboxedBlob = {
  x: number;
  y: number;
  width: number;
  height: number;
  score: number;
};

// Convert a blob in patch units back to the source image's pixel coordinates.
//
// Pipeline (per spec §4.4):
//   patch units  →  letterboxed-input pixels (× patchPx)
//   letterboxed-input pixels  →  content pixels (subtract padding)
//   content pixels  →  original-image pixels (× originalW / contentW)
//
// We compute X and Y axes independently because non-square inputs can have
// padding on only one axis. Result is clamped to image bounds because a blob
// that brushes a padded edge can extrapolate slightly past it after scaling.
export function unletterboxBlob(
  blob: Blob2DInPatchUnits,
  ctx: UnletterboxContext,
): UnletterboxedBlob {
  const patchPxW = ctx.inputW / ctx.gridW;
  const patchPxH = ctx.inputH / ctx.gridH;

  const contentW = ctx.inputW - 2 * ctx.paddingLeft;
  const contentH = ctx.inputH - 2 * ctx.paddingTop;

  if (contentW <= 0 || contentH <= 0) {
    throw new Error(
      `unletterboxBlob: invalid letterbox content dims ${contentW}x${contentH}`,
    );
  }

  const scaleX = ctx.originalW / contentW;
  const scaleY = ctx.originalH / contentH;

  const x1Input = blob.x * patchPxW;
  const y1Input = blob.y * patchPxH;
  const x2Input = (blob.x + blob.width) * patchPxW;
  const y2Input = (blob.y + blob.height) * patchPxH;

  const x1 = (x1Input - ctx.paddingLeft) * scaleX;
  const y1 = (y1Input - ctx.paddingTop) * scaleY;
  const x2 = (x2Input - ctx.paddingLeft) * scaleX;
  const y2 = (y2Input - ctx.paddingTop) * scaleY;

  const cx1 = clamp(x1, 0, ctx.originalW);
  const cy1 = clamp(y1, 0, ctx.originalH);
  const cx2 = clamp(x2, 0, ctx.originalW);
  const cy2 = clamp(y2, 0, ctx.originalH);

  return {
    x: cx1,
    y: cy1,
    width: Math.max(0, cx2 - cx1),
    height: Math.max(0, cy2 - cy1),
    score: blob.score,
  };
}

export type UnletterboxedOrientedBlob = {
  // Center in original-image pixels.
  cx: number;
  cy: number;
  // Extents along the box's local axes, in original-image pixels.
  width: number;
  height: number;
  // Radians, CCW from image-x.
  rotation: number;
  score: number;
};

// Convert an oriented blob (in patch units, centered) into original-image
// pixels. Same letterbox→content→original chain as `unletterboxBlob`, but
// the rotated geometry means we can't clamp to image bounds without changing
// the shape — callers that need clamping should slice the rotated rect
// against the image rect themselves.
//
// Non-square content/originals require axis-dependent scaling, which would
// shear a rotated box. We assume scaleX ≈ scaleY (true for any aspect-
// preserving capture); when they differ we use the average to avoid the
// shear at the cost of a slight extent mismatch. In practice DinoFast is
// always fed a frame letterboxed onto the encoder's square input, so the
// post-unletterbox scale is identical on both axes.
export function unletterboxOrientedBlob(
  blob: OrientedBlob2DInPatchUnits,
  ctx: UnletterboxContext,
): UnletterboxedOrientedBlob {
  const patchPxW = ctx.inputW / ctx.gridW;
  const patchPxH = ctx.inputH / ctx.gridH;

  const contentW = ctx.inputW - 2 * ctx.paddingLeft;
  const contentH = ctx.inputH - 2 * ctx.paddingTop;

  if (contentW <= 0 || contentH <= 0) {
    throw new Error(
      `unletterboxOrientedBlob: invalid content dims ${contentW}x${contentH}`,
    );
  }

  const scaleX = ctx.originalW / contentW;
  const scaleY = ctx.originalH / contentH;
  const scale = (scaleX + scaleY) / 2;

  const cxInput = blob.cx * patchPxW;
  const cyInput = blob.cy * patchPxH;
  const cx = (cxInput - ctx.paddingLeft) * scaleX;
  const cy = (cyInput - ctx.paddingTop) * scaleY;

  return {
    cx,
    cy,
    width: blob.width * patchPxW * scale,
    height: blob.height * patchPxH * scale,
    rotation: blob.rotation,
    score: blob.score,
  };
}

export type Point2DPx = { x: number; y: number };

// Map the CENTER of patch-grid cell (row, col) back to source-image pixels,
// using the same letterbox→content→original chain as `unletterboxBlob`. Shared
// so point-prompt coords and box coords can never drift apart.
export function patchCenterToOriginal(
  row: number,
  col: number,
  ctx: UnletterboxContext,
): Point2DPx {
  const patchPxW = ctx.inputW / ctx.gridW;
  const patchPxH = ctx.inputH / ctx.gridH;

  const contentW = ctx.inputW - 2 * ctx.paddingLeft;
  const contentH = ctx.inputH - 2 * ctx.paddingTop;

  if (contentW <= 0 || contentH <= 0) {
    throw new Error(
      `patchCenterToOriginal: invalid content dims ${contentW}x${contentH}`,
    );
  }

  const scaleX = ctx.originalW / contentW;
  const scaleY = ctx.originalH / contentH;

  const xInput = (col + 0.5) * patchPxW;
  const yInput = (row + 0.5) * patchPxH;

  return {
    x: (xInput - ctx.paddingLeft) * scaleX,
    y: (yInput - ctx.paddingTop) * scaleY,
  };
}
